import ReactPaginate, { type ReactPaginateProps } from 'react-paginate';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { useTranslation, Trans } from 'react-i18next';

export type PaginationProps = {
  pageCount: number;
  displayPerPage?: boolean;
  displayPageChange?: boolean;
  page?: number;
  perPage?: number;
  perPageOptions?: number[];
  setPage?: (page: number) => void;
  setPerPage?: (page: number) => void;
  showTotal?: boolean;
  total?: number;
  wrapperClassName?: string;
};

export function Pagination({
  page,
  setPage,
  total,
  perPage,
  displayPerPage = true,
  displayPageChange = true,
  setPerPage,
  pageCount,
  showTotal = true,
  wrapperClassName,
  perPageOptions,
  ...otherProps
}: PaginationProps & Omit<ReactPaginateProps, 'pageCount'>) {
  const { t } = useTranslation('designSystem');
  const showPageSwitch = displayPageChange && pageCount > 1;
  const showPerPage =
    displayPerPage && ((total != null && total > 5) || pageCount > 1);
  const showPagination = showPageSwitch || showPerPage;
  return showPagination ? (
    <div
      className={`flex flex-wrap justify-between gap-4 px-4 ${wrapperClassName}`}
    >
      {showPerPage && perPage != null && page != null && (
        <div className="flex flex-wrap items-center gap-2">
          <select
            className="focus:ring-br-primary rounded-md border-0 bg-transparent pr-6 text-sm focus:ring-2 focus:outline-none"
            value={perPage}
            onChange={(e) => {
              setPerPage?.(parseInt(e.target.value));
            }}
          >
            {(perPageOptions || [5, 10, 20, 30, 40, 50, 100]).map(
              (pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {t('show', { pageSize })}
                </option>
              ),
            )}
          </select>
          {showTotal && total != null && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <Trans
                t={t}
                i18nKey="showingFromToOfResults"
                values={{
                  from: page * perPage + 1,
                  to: Math.min((page + 1) * perPage, total),
                  total,
                }}
                components={{ span: <span className="font-medium" /> }}
              />
            </div>
          )}
        </div>
      )}
      {showPageSwitch && page != null && (
        <ReactPaginate
          breakLabel="..."
          breakLinkClassName="relative inline-flex items-center border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300"
          nextLabel={<ChevronRightIcon className="h-5 w-5" />}
          className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm"
          onPageChange={({ selected }) => {
            setPage?.(selected);
          }}
          previousLabel={<ChevronLeftIcon className="h-5 w-5" />}
          forcePage={page}
          pageLinkClassName="relative inline-flex items-center border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
          previousLinkClassName="relative inline-flex items-center rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
          nextLinkClassName="relative inline-flex items-center rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
          activeLinkClassName="relative z-10 inline-flex items-center border !border-br-primary !bg-br-primary/10 px-4 py-2 text-sm font-medium !text-br-primary"
          {...otherProps}
          pageCount={pageCount}
        />
      )}
    </div>
  ) : (
    <></>
  );
}
